'use client'

import { useEffect, useRef, useState, Suspense } from 'react'
import { Box, Heading, VStack, Alert, AlertDescription, AlertIcon } from '@chakra-ui/react'
import { useSearchParams } from 'next/navigation'
import { useAuth } from '@/contexts/AuthContext'
import { parseAuthCallbackFromLocation } from '@/lib/auth/authCallbackFromUrl'
import { resolvePostLoginDestination } from '@/lib/auth/postLoginDestination'
import { shouldAutoLeaveLoginForExistingSession } from '@/lib/auth/shouldAutoLeaveLoginForExistingSession'
import { isRecoverySessionPending } from '@/lib/auth/recoverySession'
import Card from '@/components/ui/Card'
import BrandLogo from '@/components/ui/BrandLogo'
import LoginForm from '@/features/auth/LoginForm'
import LoadingScreen from '@/components/ui/LoadingScreen'

/**
 * Auth boundary ownership on /login
 * ---------------------------------
 * Crossing into the app uses document navigation (window.location.replace), same
 * idea as logout, so the edge proxy sees session cookies.
 *
 * - Password: LoginForm.onSuccess → document-nav to ?next (or /).
 * - Already signed-in visit: after hydrate, document-nav home — but only if this
 *   page load did *not* start as an email callback.
 * - Email/recovery hash on /login: AuthProvider consumes and document-navs to
 *   /reset-password (etc.). /login must NOT treat the resulting currentUser as a
 *   password sign-in — that race sent recovery users to the app logged in.
 */
function readEmailCallbackOnMount(): boolean {
  if (typeof window === 'undefined') return false
  return parseAuthCallbackFromLocation({
    hash: window.location.hash,
    search: window.location.search,
  }) !== null
}

function LoginContent() {
  const searchParams = useSearchParams()
  const { currentUser, isLoading } = useAuth()
  const error = searchParams.get('error')
  const destination = resolvePostLoginDestination(searchParams.get('next'))
  // Capture once on mount — must not re-read after AuthProvider clears the hash.
  const [hadEmailCallbackOnMount] = useState(readEmailCallbackOnMount)
  const leftRef = useRef(false)

  const leave = (next: string) => {
    if (leftRef.current) return
    leftRef.current = true
    window.location.replace(next)
  }

  useEffect(() => {
    if (isRecoverySessionPending()) return
    if (
      !shouldAutoLeaveLoginForExistingSession({
        isLoading,
        hasUser: !!currentUser,
        hadEmailCallbackOnMount,
        alreadyLeft: leftRef.current,
      })
    ) {
      return
    }
    leave(destination)
  }, [isLoading, currentUser, destination, hadEmailCallbackOnMount])

  if (isRecoverySessionPending()) {
    return <LoadingScreen label="Opening reset link" />
  }

  // Recovery/invite: AuthProvider owns navigation; show wait chrome until it navigates.
  if (hadEmailCallbackOnMount && currentUser) {
    return <LoadingScreen label="Opening reset link" />
  }

  if (
    shouldAutoLeaveLoginForExistingSession({
      isLoading,
      hasUser: !!currentUser,
      hadEmailCallbackOnMount,
      alreadyLeft: false,
    })
  ) {
    return <LoadingScreen label="Redirecting" />
  }

  return (
    <Card w="full" maxW="md" rounded="xl" shadow="sm">
      <Card.Body>
        <VStack spacing={6} w="full">
          <BrandLogo />
          <Heading size="lg" color="text">
            Sign in
          </Heading>
          {error === 'link_expired' && (
            <Alert status="warning" rounded="md">
              <AlertIcon />
              <AlertDescription>
                This link has expired. Please request a new one.
              </AlertDescription>
            </Alert>
          )}
          {error === 'auth_failed' && (
            <Alert status="error" rounded="md">
              <AlertIcon />
              <AlertDescription>
                We couldn&apos;t verify your link. Please try again or request a new reset link.
              </AlertDescription>
            </Alert>
          )}
          <LoginForm onSuccess={() => leave(destination)} />
        </VStack>
      </Card.Body>
    </Card>
  )
}

export default function LoginPage() {
  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="app.bg" p={{ base: 4, sm: 6 }}>
      <Suspense fallback={null}>
        <LoginContent />
      </Suspense>
    </Box>
  )
}
